import { Controller } from "@hotwired/stimulus"
import { useClickOutside } from "stimulus-use"

export default class extends Controller {
  static targets = [
    "mainButton",
    "mainButtonText",
    "assetIcon",
    "assetIconWrapper",
    "input",
    "chevron",
    "panel",
    "search",
    "item",
    "error",
  ]

  static values = {
    placeholder: { type: String, default: "None" },
    autoSubmit: { type: Boolean, default: false },
  }

  connect() {
    useClickOutside(this)
  }

  clickOutside() {
    if (this.isOpen()) {
      this.close()
      this.chevronTarget.focus()
    }
  }

  toggle(event) {
    event.preventDefault()
    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.panelTarget.classList.add("record-picker__panel--open")
    this.panelTarget.setAttribute("aria-hidden", "false")
    this.chevronTarget.setAttribute("aria-expanded", "true")
    this.searchTarget.value = ""
    this._applyFilter("")
    this.searchTarget.focus()
  }

  close() {
    this.panelTarget.classList.remove("record-picker__panel--open")
    this.panelTarget.setAttribute("aria-hidden", "true")
    this.chevronTarget.setAttribute("aria-expanded", "false")
  }

  isOpen() {
    return this.panelTarget.classList.contains("record-picker__panel--open")
  }

  guardNavigation(event) {
    if (this.mainButtonTarget.classList.contains("disabled")) {
      event.preventDefault()
    }
  }

  filter(event) {
    this._applyFilter(event?.target?.value ?? "")
  }

  handleKeydown(event) {
    if (!this.isOpen()) return

    if (event.key === "Escape") {
      this.close()
      this.chevronTarget.focus()
    } else if (event.key === "Enter") {
      const item = document.activeElement?.closest("[data-record-picker-target='item']")
      if (item) {
        event.preventDefault()
        item.click()
      }
    }
  }

  selectItem(event) {
    const item = event.target.closest("[data-record-picker-target='item']")
    if (!item) return
    event.preventDefault()
    this._applySelection(item)
    this.close()
    this.mainButtonTarget.focus()
  }

  // --- private ---------------------------------------------------------

  _applyFilter(query) {
    const q = query.toLowerCase().trim()
    this.itemTargets.forEach((el) => {
      const name = (el.dataset.name || "").toLowerCase()
      const detail = (el.dataset.detail || "").toLowerCase()
      const hidden = q.length > 0 && !name.includes(q) && !detail.includes(q)
      el.classList.toggle("record-picker__item--hidden", hidden)
    })
  }

  _applySelection(item) {
    const id = item.dataset.id ?? ""
    const name = item.dataset.name ?? ""
    const url = item.dataset.url ?? ""
    const displayText = url ? name : this.placeholderValue
    this.mainButtonTextTarget.textContent = displayText
    this.mainButtonTarget.href = url || "#"
    this.mainButtonTarget.setAttribute("aria-label", displayText)
    if (url) {
      this.mainButtonTarget.classList.remove("disabled")
      this.mainButtonTarget.removeAttribute("aria-disabled")
      this.assetIconWrapperTarget.classList.remove("hidden")
      this.assetIconTarget.className = item.dataset.icon || ""
      this.assetIconTarget.style.color = item.dataset.iconColor || ""
      this.assetIconWrapperTarget.style.background = item.dataset.color || ""
    } else {
      this.mainButtonTarget.classList.add("disabled")
      this.mainButtonTarget.setAttribute("aria-disabled", "true")
      this.assetIconWrapperTarget.classList.add("hidden")
      this.assetIconTarget.className = ""
      this.assetIconTarget.style.color = ""
      this.assetIconWrapperTarget.style.background = ""
    }

    this.itemTargets.forEach((el) => {
      const selected = el === item
      el.classList.toggle("record-picker__item--selected", selected)
      el.setAttribute("aria-pressed", selected)
    })

    this.errorTarget.textContent = ""
    if (this.hasInputTarget) {
      this.inputTarget.value = id
      if (this.autoSubmitValue) {
        this.element.closest("form")?.requestSubmit()
      }
    }
    this.dispatch("change", { detail: { id, name, url } })
  }
}
